import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AppDialog,
  Select,
  type SelectOption,
  MultiSelect,
  type MultiSelectOption,
  Textarea,
  Button,
  StatusPill,
  Checkbox,
  TextInput,
  MiniLoader,
} from '../Common'
import type { StatusPillTone } from '../Common/StatusPill'
import { useNls } from '../../nls/nlsProvider'
import type { TimelineElement } from '../../types/timelineElement'
import type { EduRoom, EduSessionSeries, EduBooking, EduEntitlement } from '../../types/schema'
import type { TimetableOccurrence } from './timetableHelpers'
import type { EducationKind } from './EducationHub'
import { updateEduSession, type UpdateEduSessionAction } from '../../services/eduTimetableService'
import {
  listEduSessionBookings,
  bookEduSession,
  moveEduBooking,
  cancelEduBooking,
  leaveEduWaitlist,
  listEduLearnerEntitlements,
  listEduBookingSessions,
} from '../../services/eduBookingService'
import {
  listLearners,
  listActiveEnrolmentsForNodes,
  type EduLearnerDoc,
} from '../../services/eduLearnerService'
import { mapEduServerError } from './classesBuilderHelpers'
import { EduRegisterDialog } from './EduRegisterDialog'
import {
  workspaceToday,
  canTakeAttendance as checkCanTakeAttendance,
  isClassTeacher,
} from '../../utils/eduAttendance'
import './eduSessionDialog.css'

function extractServerErrorCode(err: unknown): string {
  const e = err as { details?: { code?: string }; code?: string; message?: string }
  const code = e?.details?.code || e?.code
  const message = e?.message || ''
  const bracket = typeof message === 'string' ? message.match(/\[([A-Z0-9_]+)\]/) : null
  const eff = (code || bracket?.[1] || '').replace(/^functions\//, '')
  if (eff) return eff
  return message || 'An error occurred'
}

export interface EduSessionDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  activeRole?: string
  currentUserId?: string | null
  isOwner?: boolean
  /** Workspace IANA timezone string for computing 'today' (EQ52). Defaults to Asia/Kolkata. */
  timezone?: string | null
  elements: TimelineElement[]
  rooms: EduRoom[]
  directoryMembers?: Array<{ uid: string; displayName?: string; name?: string; role?: string }>
  occurrence: TimetableOccurrence | null
  series?: EduSessionSeries | null
  kind?: EducationKind
  onEditSlot?: () => void
  onSessionUpdated?: () => void
}

export const EduSessionDialog = ({
  isOpen,
  onClose,
  workspaceId,
  activeRole,
  currentUserId,
  isOwner = false,
  timezone,
  elements,
  rooms,
  directoryMembers = [],
  occurrence,
  series,
  kind,
  onEditSlot,
  onSessionUpdated,
}: EduSessionDialogProps) => {
  const { t } = useNls()

  const [substituteUid, setSubstituteUid] = useState<string>(() => occurrence?.substituteUid || '')
  const [topicItemIds, setTopicItemIds] = useState<string[]>(() => occurrence?.topicItemIds || [])
  const [note, setNote] = useState<string>(() => occurrence?.note || '')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRegisterOpen, setIsRegisterOpen] = useState(false)

  const [prevOccurrenceId, setPrevOccurrenceId] = useState(occurrence?.id)
  if (occurrence?.id !== prevOccurrenceId) {
    setPrevOccurrenceId(occurrence?.id)
    setSubstituteUid(occurrence?.substituteUid || '')
    setTopicItemIds(occurrence?.topicItemIds || [])
    setNote(occurrence?.note || '')
    setErrorMessage(null)
  }

  const classElement = useMemo(() => {
    const classId = series?.classId || occurrence?.classId
    return elements.find((el) => el.id === classId)
  }, [elements, series?.classId, occurrence?.classId])

  const subjectElement = useMemo(() => {
    const nodeId = series?.nodeId || occurrence?.nodeId
    return elements.find((el) => el.id === nodeId)
  }, [elements, series?.nodeId, occurrence?.nodeId])

  const roomObj = useMemo(() => {
    const rId = series?.roomId || occurrence?.roomId
    return rooms.find((r) => r.id === rId)
  }, [rooms, series?.roomId, occurrence?.roomId])

  const isCurrentClassTeacher = useMemo(() => {
    return isClassTeacher({ uid: currentUserId, classElement })
  }, [currentUserId, classElement])

  const isAdmin = useMemo(() => {
    return isOwner || activeRole === 'ADMIN'
  }, [isOwner, activeRole])

  const isTimetableEditor = useMemo(() => {
    return isAdmin || isCurrentClassTeacher
  }, [isAdmin, isCurrentClassTeacher])

  const isSeriesTeacher = useMemo(() => {
    if (!currentUserId) return false
    const teacherUids = series?.teacherUids || occurrence?.teacherUids || []
    return teacherUids.includes(currentUserId)
  }, [currentUserId, series?.teacherUids, occurrence?.teacherUids])

  const isCurrentSubstitute = useMemo(() => {
    if (!currentUserId) return false
    return occurrence?.substituteUid === currentUserId
  }, [currentUserId, occurrence?.substituteUid])

  const canManageSession = isTimetableEditor
  const canSetTopics = isTimetableEditor || isSeriesTeacher || isCurrentSubstitute

  const isBookable = Boolean(series?.bookable && (!kind || kind === 'TRAINING'))
  const capacity = series?.capacity ?? 30

  const rootElement = useMemo(() => {
    if (classElement?.parentId) {
      const parent = elements.find((el) => el.id === classElement.parentId)
      if (parent?.type === 'GOAL') return parent
    }
    if (subjectElement?.parentId) {
      const parent = elements.find((el) => el.id === subjectElement.parentId)
      if (parent?.type === 'GOAL') return parent
      if (parent?.parentId) {
        const grandParent = elements.find((el) => el.id === parent.parentId)
        if (grandParent?.type === 'GOAL') return grandParent
      }
    }
    return elements.find((el) => el.type === 'GOAL' && el.edu) || null
  }, [elements, classElement, subjectElement])
  const rootId = rootElement?.id || ''

  // Bookings state
  const [bookings, setBookings] = useState<EduBooking[]>([])
  const [isLoadingBookings, setIsLoadingBookings] = useState(false)
  const [bookingActionError, setBookingActionError] = useState<string | null>(null)

  // Sub-modal states: Book for
  const [isBookModalOpen, setIsBookModalOpen] = useState(false)
  const [trainees, setTrainees] = useState<EduLearnerDoc[]>([])
  const [selectedTraineeId, setSelectedTraineeId] = useState<string>('')
  const [traineeEntitlements, setTraineeEntitlements] = useState<EduEntitlement[]>([])
  const [selectedEntitlementId, setSelectedEntitlementId] = useState<string>('auto')
  const [bookDropIn, setBookDropIn] = useState<boolean>(false)
  const [bookWaitlist, setBookWaitlist] = useState<boolean>(false)
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false)

  // Sub-modal states: Move
  const [moveTargetBooking, setMoveTargetBooking] = useState<EduBooking | null>(null)
  const [availableSessions, setAvailableSessions] = useState<Array<{ seriesId: string; date: string; startTime: string; label: string }>>([])
  const [selectedMoveSessionKey, setSelectedMoveSessionKey] = useState<string>('')
  const [isMovingSubmitting, setIsMovingSubmitting] = useState(false)

  // Sub-modal states: Cancel
  const [cancelTargetBooking, setCancelTargetBooking] = useState<EduBooking | null>(null)
  const [cancelReason, setCancelReason] = useState<string>('')
  const [waiveLateCancel, setWaiveLateCancel] = useState<boolean>(false)
  const [waiveReason, setWaiveReason] = useState<string>('')
  const [isCancellingSubmitting, setIsCancellingSubmitting] = useState(false)

  const [isInsideCancelWindow, setIsInsideCancelWindow] = useState(false)

  const fetchBookings = useCallback(async () => {
    if (!isBookable || !occurrence?.seriesId || !occurrence?.date) return
    setIsLoadingBookings(true)
    try {
      const res = await listEduSessionBookings({
        workspaceId,
        seriesId: occurrence.seriesId,
        date: occurrence.date,
      })
      setBookings(res.bookings || [])
      setBookingActionError(null)
    } catch (err) {
      setBookingActionError(extractServerErrorCode(err))
    } finally {
      setIsLoadingBookings(false)
    }
  }, [isBookable, occurrence, workspaceId])

  useEffect(() => {
    if (isOpen && isBookable) {
      void Promise.resolve().then(() => {
        void fetchBookings()
      })
    }
  }, [isOpen, isBookable, fetchBookings])

  const handleOpenBookModal = async () => {
    setIsBookModalOpen(true)
    setBookingActionError(null)
    setSelectedTraineeId('')
    setTraineeEntitlements([])
    setSelectedEntitlementId('auto')
    setBookDropIn(false)
    setBookWaitlist(false)
    try {
      // F-ED10b1-2: List only trainees with an active enrolment in this programme
      // using the same enrolment rule as the server's booking check (bookings.ts:858-870)
      const targetNodeIds = [series?.nodeId, series?.classId, series?.rootId].filter(Boolean) as string[]
      const [allLearners, activeEnrols] = await Promise.all([
        listLearners(workspaceId),
        targetNodeIds.length > 0 ? listActiveEnrolmentsForNodes(workspaceId, targetNodeIds) : Promise.resolve([]),
      ])
      const sessionDate = occurrence?.date
      const targetNodeSet = new Set(targetNodeIds)
      const activeLearnerIds = new Set(
        activeEnrols
          .filter((enrol) => {
            if (!targetNodeSet.has(enrol.nodeId)) return false
            const fromVal = enrol.from as string | undefined
            const toVal = enrol.to as string | undefined
            const fromValid = !fromVal || !sessionDate || fromVal <= sessionDate
            const toValid = !toVal || !sessionDate || toVal >= sessionDate
            return fromValid && toValid
          })
          .map((enrol) => enrol.learnerId),
      )
      setTrainees(allLearners.filter((l) => l.status !== 'LEFT' && activeLearnerIds.has(l.id)))
    } catch (err: unknown) {
      setBookingActionError(extractServerErrorCode(err))
    }
  }

  useEffect(() => {
    if (!selectedTraineeId) return
    let active = true
    listEduLearnerEntitlements({ workspaceId, learnerId: selectedTraineeId })
      .then((res) => {
        if (!active) return
        setTraineeEntitlements(res.entitlements || [])
      })
      .catch(() => {
        if (!active) return
        setTraineeEntitlements([])
      })
    return () => {
      active = false
    }
  }, [selectedTraineeId, workspaceId])

  const handleConfirmBook = async () => {
    if (!selectedTraineeId || !occurrence?.seriesId || !occurrence?.date) return
    setIsBookingSubmitting(true)
    setBookingActionError(null)
    try {
      await bookEduSession({
        workspaceId,
        seriesId: occurrence.seriesId,
        date: occurrence.date,
        learnerId: selectedTraineeId,
        entitlementId:
          selectedEntitlementId && selectedEntitlementId !== 'auto' && !bookDropIn
            ? selectedEntitlementId
            : undefined,
        dropIn: bookDropIn,
        waitlist: bookWaitlist,
      })
      setIsBookModalOpen(false)
      fetchBookings()
      onSessionUpdated?.()
    } catch (err) {
      setBookingActionError(extractServerErrorCode(err))
    } finally {
      setIsBookingSubmitting(false)
    }
  }

  const handleOpenMoveModal = async (booking: EduBooking) => {
    setMoveTargetBooking(booking)
    setBookingActionError(null)
    setSelectedMoveSessionKey('')
    try {
      const fromDate = occurrence?.date || new Date().toISOString().slice(0, 10)
      const toDateObj = new Date(fromDate)
      toDateObj.setDate(toDateObj.getDate() + 28)
      const toDate = toDateObj.toISOString().slice(0, 10)
      if (rootId) {
        const res = await listEduBookingSessions({
          workspaceId,
          rootId,
          fromDate,
          toDate,
        })
        const items = (res.sessions || [])
          .filter(
            (s) =>
              !(s.seriesId === occurrence?.seriesId && s.date === occurrence?.date),
          )
          .map((s) => ({
            seriesId: s.seriesId,
            date: s.date,
            startTime: s.startTime,
            label: `${s.date} ${s.startTime} (${s.bookedCount}/${s.capacity})`,
          }))
        setAvailableSessions(items)
      }
    } catch {
      setAvailableSessions([])
    }
  }

  const handleConfirmMove = async () => {
    if (!moveTargetBooking || !selectedMoveSessionKey) return
    const [toSeriesId, toDate] = selectedMoveSessionKey.split('___')
    if (!toSeriesId || !toDate) return
    setIsMovingSubmitting(true)
    setBookingActionError(null)
    try {
      await moveEduBooking({
        workspaceId,
        bookingId: moveTargetBooking.id,
        toSeriesId,
        toDate,
      })
      setMoveTargetBooking(null)
      fetchBookings()
      onSessionUpdated?.()
    } catch (err) {
      setBookingActionError(extractServerErrorCode(err))
    } finally {
      setIsMovingSubmitting(false)
    }
  }

  const handleOpenCancelModal = (booking: EduBooking) => {
    setCancelTargetBooking(booking)
    setCancelReason('')
    setWaiveLateCancel(false)
    setWaiveReason('')
    setBookingActionError(null)
    setIsInsideCancelWindow(Boolean(booking.lateCancelNow && !booking.freeCancelOnce))
  }

  const handleConfirmCancel = async () => {
    if (!cancelTargetBooking) return
    setIsCancellingSubmitting(true)
    setBookingActionError(null)
    try {
      await cancelEduBooking({
        workspaceId,
        bookingId: cancelTargetBooking.id,
        reason: cancelReason.trim() || undefined,
        waiveFee: waiveLateCancel || undefined,
        waiveReason: waiveLateCancel ? waiveReason.trim() : undefined,
      })
      setCancelTargetBooking(null)
      setCancelReason('')
      setWaiveLateCancel(false)
      setWaiveReason('')
      fetchBookings()
      onSessionUpdated?.()
    } catch (err) {
      setBookingActionError(extractServerErrorCode(err))
    } finally {
      setIsCancellingSubmitting(false)
    }
  }

  const handleLeaveWaitlist = async (booking: EduBooking) => {
    setBookingActionError(null)
    try {
      await leaveEduWaitlist({
        workspaceId,
        bookingId: booking.id,
      })
      fetchBookings()
      onSessionUpdated?.()
    } catch (err) {
      setBookingActionError(extractServerErrorCode(err))
    }
  }

  const activeBookings = useMemo(() => {
    return bookings.filter(
      (b) =>
        b.status === 'BOOKED' ||
        b.status === 'ATTENDED' ||
        b.status === 'NO_SHOW' ||
        b.status === 'LATE_CANCEL',
    )
  }, [bookings])

  const waitlistBookings = useMemo(() => {
    return bookings
      .filter((b) => b.status === 'WAITLIST')
      .sort((a, b) => (a.waitlistPosition ?? 999) - (b.waitlistPosition ?? 999))
  }, [bookings])

  // EQ52: who can take attendance
  const todayIso = workspaceToday(timezone)
  const isSessionToday = occurrence?.date === todayIso
  const canTakeAttendance = useMemo(() => {
    if (!occurrence) return false
    return checkCanTakeAttendance({
      date: occurrence.date,
      todayIso,
      status: occurrence.status,
      hasSeries: Boolean(occurrence.seriesId),
      isAdmin,
      isClassTeacher: isCurrentClassTeacher,
      isSeriesTeacher,
      isSubstitute: isCurrentSubstitute,
    })
  }, [
    occurrence,
    todayIso,
    isAdmin,
    isCurrentClassTeacher,
    isSeriesTeacher,
    isCurrentSubstitute,
  ])

  // Available substitutes: staff >= EDITOR, excluding the series' teachers
  const availableSubstitutes = useMemo<SelectOption<string>[]>(() => {
    const teacherUids = new Set(series?.teacherUids || occurrence?.teacherUids || [])
    return directoryMembers
      .filter((m) => {
        const r = m.role?.toUpperCase()
        const isStaff = r === 'EDITOR' || r === 'ADMIN' || r === 'OWNER'
        return isStaff && !teacherUids.has(m.uid)
      })
      .map((m) => ({
        value: m.uid,
        label: m.displayName || m.name || m.uid,
      }))
  }, [directoryMembers, series?.teacherUids, occurrence?.teacherUids])

  // Topics: child elements under nodeId (≤ 20)
  const availableTopics = useMemo<MultiSelectOption<string>[]>(() => {
    const nodeId = series?.nodeId || occurrence?.nodeId
    if (!nodeId) return []
    return elements
      .filter((el) => el.parentId === nodeId)
      .map((el) => ({
        value: el.id,
        label: el.title,
      }))
  }, [elements, series?.nodeId, occurrence?.nodeId])

  const teacherNames = useMemo(() => {
    const teacherUids = series?.teacherUids || occurrence?.teacherUids || []
    return teacherUids
      .map((uId) => {
        const m = directoryMembers.find((mem) => mem.uid === uId)
        return m?.displayName || m?.name || uId
      })
      .join(', ')
  }, [directoryMembers, series?.teacherUids, occurrence?.teacherUids])

  const currentSubName = useMemo(() => {
    if (!occurrence?.substituteUid) return null
    const m = directoryMembers.find((mem) => mem.uid === occurrence.substituteUid)
    return m?.displayName || m?.name || occurrence.substituteUid
  }, [directoryMembers, occurrence])

  const onlineUrl = series?.onlineUrl || occurrence?.onlineUrl

  const handleAction = async (action: UpdateEduSessionAction, payload?: {
    substituteUid?: string
    topicItemIds?: string[]
    note?: string
  }) => {
    if (!occurrence || !occurrence.seriesId) return
    setIsSubmitting(true)
    setErrorMessage(null)
    try {
      await updateEduSession({
        workspaceId,
        seriesId: occurrence.seriesId,
        date: occurrence.date,
        action,
        substituteUid: payload?.substituteUid,
        topicItemIds: payload?.topicItemIds,
        note: payload?.note,
      })
      onSessionUpdated?.()
    } catch (err: unknown) {
      setErrorMessage(mapEduServerError(err, t))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!occurrence) return null

  const isCancelled = occurrence.status === 'CANCELLED'

  return (
    <>
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={t('NLSEducationSessionDialogTitle')}
      size="md"
      actions={[]}
    >
      <div className="sessionDialogRoot" data-testid="edu-session-dialog">
        {errorMessage && (
          <div className="sessionDialogError" data-testid="session-error-banner" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="sessionDialogMetaGrid">
          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionTime')}</span>
            <span className="sessionDialogMetaValue">
              {occurrence.date} • {occurrence.startTime} ({occurrence.durationMin}m)
            </span>
          </div>

          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionStatus')}</span>
            <span
              className={`sessionDialogStatusBadge sessionDialogStatusBadge--${
                isCancelled ? 'cancelled' : 'planned'
              }`}
            >
              {isCancelled
                ? t('NLSEducationSessionStatusCancelled')
                : t('NLSEducationSessionStatusPlanned')}
            </span>
          </div>

          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionClassSubject')}</span>
            <span className="sessionDialogMetaValue">
              {subjectElement?.title || classElement?.title || t('NLSEducationCalendarClassSessionFallback')}
              {subjectElement && classElement ? ` (${classElement.title})` : ''}
            </span>
          </div>

          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionTeachers')}</span>
            <span className="sessionDialogMetaValue">{teacherNames || '—'}</span>
          </div>

          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionRoom')}</span>
            <span className="sessionDialogMetaValue">{roomObj?.name || '—'}</span>
          </div>

          <div className="sessionDialogMetaItem">
            <span className="sessionDialogMetaLabel">{t('NLSEducationSessionSubstitute')}</span>
            <span className="sessionDialogMetaValue">
              {currentSubName ? `Sub: ${currentSubName}` : t('NLSEducationSessionNoSubstitute')}
            </span>
          </div>
        </div>

        {/* Online link (Join button) */}
        {onlineUrl && (
          <div className="sessionDialogJoinRow">
            <a
              href={onlineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sessionDialogJoinLink"
              role="link"
            >
              {t('NLSEducationSessionJoin')}
            </a>
          </div>
        )}

        {/* Substitute section for Timetable Editors */}
        {canManageSession && (
          <section className="sessionDialogSection" data-testid="session-substitute-control">
            <span className="sessionDialogSectionTitle">
              {t('NLSEducationSessionSubstitute')}
            </span>
            <div className="sessionDialogSubRow">
              <div className="sessionDialogSubSelect" data-testid="session-substitute-select">
                <Select<string>
                  value={substituteUid}
                  onChange={(val) => setSubstituteUid(String(val))}
                  options={[
                    { value: '', label: t('NLSEducationSessionSubstituteSelectPlaceholder') },
                    ...availableSubstitutes,
                  ]}
                  ariaLabel={t('NLSEducationSessionSubstitute')}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={!substituteUid || substituteUid === occurrence.substituteUid || isSubmitting}
                onClick={() => handleAction('SET_SUBSTITUTE', { substituteUid })}
                data-testid="session-save-substitute-btn"
              >
                {t('NLSEducationSessionSaveSubstitute')}
              </Button>
              {occurrence.substituteUid && (
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={isSubmitting}
                  onClick={() => handleAction('CLEAR_SUBSTITUTE')}
                  data-testid="session-clear-substitute-btn"
                >
                  {t('NLSEducationSessionClearSubstitute')}
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Topics & note section for Editors / Teachers / Substitutes */}
        {canSetTopics && (
          <section className="sessionDialogSection" data-testid="session-topics-control">
            <span className="sessionDialogSectionTitle">
              {t('NLSEducationSessionTopics')}
            </span>
            {availableTopics.length > 0 && (
              <MultiSelect
                label={t('NLSEducationSessionTopics')}
                selectedValues={topicItemIds}
                onChange={(next) => setTopicItemIds(next.slice(0, 20))}
                options={availableTopics}
                placeholder={t('NLSEducationSessionTopicsPlaceholder')}
              />
            )}
            <Textarea
              value={note}
              onChange={(next) => setNote(next.slice(0, 500))}
              placeholder={t('NLSEducationSessionNotePlaceholder')}
              maxLength={500}
              aria-label={t('NLSEducationSessionNote')}
              data-testid="session-note-input"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={isSubmitting}
                onClick={() => handleAction('SET_TOPICS', { topicItemIds, note })}
                data-testid="session-save-topics-btn"
              >
                {t('NLSEducationSessionSaveTopics')}
              </Button>
            </div>
          </section>
        )}

        {/* Bookings Section (EQ164) */}
        {isBookable && (
          <section className="sessionDialogSection sessionDialogSection--bookings" data-testid="session-bookings-section">
            <div className="sessionDialogBookingsHeader">
              <span className="sessionDialogSectionTitle">{t('NLSEducationBookingSectionTitle')}</span>
              <div className="sessionDialogBookingsMeta">
                <span data-testid="bookings-count-booked">
                  {t('NLSEducationBookingCounts', { booked: activeBookings.length, capacity })}
                </span>
                <span>•</span>
                <span data-testid="bookings-count-waitlist">
                  {t('NLSEducationBookingWaitlistCount', { count: waitlistBookings.length })}
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleOpenBookModal}
                data-testid="session-book-for-btn"
              >
                {t('NLSEducationBookingActionBookFor')}
              </Button>
            </div>

            {bookingActionError && (
              <div className="sessionDialogError" role="alert" data-testid="booking-action-error">
                {bookingActionError}
              </div>
            )}

            {isLoadingBookings ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                <MiniLoader />
              </div>
            ) : (
              <>
                {/* Booked roster */}
                <div style={{ marginTop: '8px' }}>
                  <span className="sessionDialogMetaLabel">{t('NLSEducationBookingRosterTitle')}</span>
                  {activeBookings.length === 0 ? (
                    <div className="sessionDialogEmptyState">{t('NLSEducationBookingEmptyRoster')}</div>
                  ) : (
                    <div className="sessionDialogRoster">
                      {activeBookings.map((b) => {
                        let entLabel = '—'
                        if (b.entitlementType === 'PACK') {
                          entLabel = b.sessionsRemainingSnapshot != null ? `Pack (${b.sessionsRemainingSnapshot} left)` : 'Pack'
                        } else if (b.entitlementType === 'MEMBERSHIP') {
                          entLabel = 'Membership'
                        } else if (b.entitlementType === 'DROP_IN' || b.dropInDueId) {
                          entLabel = 'Drop-in'
                        } else if (b.entitlementType === 'CREDIT') {
                          entLabel = 'Credit'
                        }

                        let statusPillTone: StatusPillTone = 'info'
                        let statusText = t('NLSEducationBookingStatusBooked')
                        if (b.status === 'ATTENDED') {
                          statusPillTone = 'success'
                          statusText = t('NLSEducationBookingStatusAttended')
                        } else if (b.status === 'NO_SHOW') {
                          statusPillTone = 'danger'
                          statusText = t('NLSEducationBookingStatusNoShow')
                        } else if (b.status === 'LATE_CANCEL') {
                          statusPillTone = 'neutral'
                          statusText = t('NLSEducationBookingStatusLateCancel')
                        }

                        return (
                          <div key={b.id} className="sessionDialogRosterRow" data-testid={`booking-row-${b.id}`}>
                            <div className="sessionDialogRosterLeft">
                              <span className="sessionDialogTraineeName">{b.learnerName || b.learnerId}</span>
                              <span className="sessionDialogEntitlementTag">{entLabel}</span>
                              <StatusPill tone={statusPillTone} compact>
                                {statusText}
                              </StatusPill>
                            </div>
                            <div className="sessionDialogRosterActions">
                              <Button
                                variant="tertiary"
                                size="sm"
                                onClick={() => handleOpenMoveModal(b)}
                                data-testid={`booking-move-btn-${b.id}`}
                              >
                                {t('NLSEducationBookingActionMove')}
                              </Button>
                              <Button
                                variant="tertiary"
                                size="sm"
                                onClick={() => handleOpenCancelModal(b)}
                                data-testid={`booking-cancel-btn-${b.id}`}
                              >
                                {t('NLSEducationBookingActionCancel')}
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Waitlist in order */}
                <div style={{ marginTop: '12px' }}>
                  <span className="sessionDialogMetaLabel">{t('NLSEducationBookingWaitlistTitle')}</span>
                  {waitlistBookings.length === 0 ? (
                    <div className="sessionDialogEmptyState">{t('NLSEducationBookingEmptyWaitlist')}</div>
                  ) : (
                    <div className="sessionDialogRoster">
                      {waitlistBookings.map((b, idx) => (
                        <div key={b.id} className="sessionDialogWaitlistRow" data-testid={`waitlist-row-${b.id}`}>
                          <div className="sessionDialogRosterLeft">
                            <span className="sessionDialogWaitlistBadge">
                              {t('NLSEducationBookingWaitlistPos', { pos: b.waitlistPosition ?? (idx + 1) })}
                            </span>
                            <span className="sessionDialogTraineeName">{b.learnerName || b.learnerId}</span>
                            {b.lastPromotionSkippedReason && (
                              <span className="sessionDialogEntitlementTag" title={b.lastPromotionSkippedReason}>
                                {b.lastPromotionSkippedReason}
                              </span>
                            )}
                          </div>
                          <div className="sessionDialogRosterActions">
                            <Button
                              variant="tertiary"
                              size="sm"
                              onClick={() => handleLeaveWaitlist(b)}
                              data-testid={`waitlist-remove-btn-${b.id}`}
                            >
                              {t('NLSEducationBookingActionLeaveWaitlist')}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {/* Actions bar */}
        <div className="sessionDialogActionsBar">
          <div className="sessionDialogActionsLeft">
            {canTakeAttendance && (
              <Button
                variant="secondary"
                size="sm"
                disabled={isSubmitting}
                onClick={() => setIsRegisterOpen(true)}
                data-testid="session-take-attendance-btn"
              >
                {t('NLSEducationSessionTakeAttendance')}
              </Button>
            )}
            {canManageSession && (
              <>
                {isCancelled ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={() => handleAction('RESTORE')}
                    data-testid="session-restore-btn"
                  >
                    {t('NLSEducationSessionRestoreSession')}
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={() => handleAction('CANCEL')}
                    data-testid="session-cancel-btn"
                  >
                    {t('NLSEducationSessionCancelSession')}
                  </Button>
                )}

                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={isSubmitting}
                  onClick={() => {
                    onClose()
                    onEditSlot?.()
                  }}
                  data-testid="session-edit-slot-btn"
                >
                  {t('NLSEducationSessionEditSlot')}
                </Button>
              </>
            )}
          </div>

          <div className="sessionDialogActionsRight">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('NLSEducationSessionClose')}
            </Button>
          </div>
        </div>
      </div>
    </AppDialog>

    {/* Book for Modal */}
    {isBookModalOpen && (
      <AppDialog
        open={isBookModalOpen}
        onDismiss={() => setIsBookModalOpen(false)}
        title={t('NLSEducationBookingModalBookTitle')}
        size="sm"
        actions={[
          {
            label: t('NLSEducationSlotEditorClose'),
            variant: 'secondary',
            onClick: () => setIsBookModalOpen(false),
          },
          {
            label: t('NLSEducationBookingConfirm'),
            variant: 'primary',
            disabled: !selectedTraineeId || isBookingSubmitting,
            loading: isBookingSubmitting,
            onClick: handleConfirmBook,
          },
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {bookingActionError && (
            <div className="sessionDialogError" role="alert">
              {bookingActionError}
            </div>
          )}
          <Select<string>
            ariaLabel={t('NLSEducationBookingPickerTrainee')}
            value={selectedTraineeId || 'none'}
            options={[
              { value: 'none', label: `-- ${t('NLSEducationBookingPickerTrainee')} --` },
              ...trainees.map((tr) => ({
                value: tr.id,
                label: tr.displayName || tr.id,
              })),
            ]}
            onChange={(val) => setSelectedTraineeId(val === 'none' ? '' : val)}
          />

          {selectedTraineeId && !bookDropIn && !bookWaitlist && (
            <Select<string>
              ariaLabel={t('NLSEducationBookingPickerEntitlement')}
              value={selectedEntitlementId}
              options={[
                { value: 'auto', label: t('NLSEducationBookingAutoPick') },
                ...traineeEntitlements.map((e) => {
                  const left = e.sessionsTotal != null ? Math.max(0, e.sessionsTotal - (e.sessionsUsed || 0) - (e.sessionsHeld || 0)) : null
                  return {
                    value: e.id,
                    label: `${e.kind} (${e.status})${left != null ? ` — ${left} left` : ''}`,
                  }
                }),
              ]}
              onChange={(val) => setSelectedEntitlementId(val)}
            />
          )}

          <Checkbox
            label={t('NLSEducationBookingDropInOption')}
            checked={bookDropIn}
            onChange={(checked) => {
              setBookDropIn(checked)
              if (checked) setBookWaitlist(false)
            }}
          />

          <Checkbox
            label={t('NLSEducationBookingJoinWaitlist')}
            checked={bookWaitlist}
            onChange={(checked) => {
              setBookWaitlist(checked)
              if (checked) setBookDropIn(false)
            }}
          />
        </div>
      </AppDialog>
    )}

    {/* Move Booking Modal */}
    {Boolean(moveTargetBooking) && (
      <AppDialog
        open={Boolean(moveTargetBooking)}
        onDismiss={() => setMoveTargetBooking(null)}
        title={t('NLSEducationBookingModalMoveTitle')}
        size="sm"
        actions={[
          {
            label: t('NLSEducationSlotEditorClose'),
            variant: 'secondary',
            onClick: () => setMoveTargetBooking(null),
          },
          {
            label: t('NLSEducationBookingConfirm'),
            variant: 'primary',
            disabled: !selectedMoveSessionKey || isMovingSubmitting,
            loading: isMovingSubmitting,
            onClick: handleConfirmMove,
          },
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {bookingActionError && (
            <div className="sessionDialogError" role="alert">
              {bookingActionError}
            </div>
          )}
          <Select<string>
            ariaLabel={t('NLSEducationBookingMoveTargetSession')}
            value={selectedMoveSessionKey || 'none'}
            options={[
              { value: 'none', label: `-- ${t('NLSEducationBookingMoveTargetSession')} --` },
              ...availableSessions.map((s) => ({
                value: `${s.seriesId}___${s.date}`,
                label: s.label,
              })),
            ]}
            onChange={(val) => setSelectedMoveSessionKey(val === 'none' ? '' : val)}
          />
        </div>
      </AppDialog>
    )}

    {/* Cancel Booking Modal */}
    {Boolean(cancelTargetBooking) && (
      <AppDialog
        open={Boolean(cancelTargetBooking)}
        onDismiss={() => setCancelTargetBooking(null)}
        title={t('NLSEducationBookingModalCancelTitle')}
        size="sm"
        actions={[
          {
            label: t('NLSEducationSlotEditorClose'),
            variant: 'secondary',
            onClick: () => setCancelTargetBooking(null),
          },
          {
            label: t('NLSEducationBookingConfirm'),
            variant: 'danger',
            disabled: isCancellingSubmitting || (waiveLateCancel && !waiveReason.trim()),
            loading: isCancellingSubmitting,
            onClick: handleConfirmCancel,
          },
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {bookingActionError && (
            <div className="sessionDialogError" role="alert">
              {bookingActionError}
            </div>
          )}
          <TextInput
            aria-label={t('NLSEducationBookingCancelReason')}
            placeholder={t('NLSEducationBookingCancelReasonPlaceholder')}
            value={cancelReason}
            onChange={(val) => setCancelReason(val)}
          />

          {isAdmin && isInsideCancelWindow && (
            <>
              <Checkbox
                label={t('NLSEducationBookingWaiveLate')}
                checked={waiveLateCancel}
                onChange={(checked) => setWaiveLateCancel(checked)}
              />
              {waiveLateCancel && (
                <TextInput
                  aria-label={t('NLSEducationBookingWaiveReason')}
                  placeholder={t('NLSEducationBookingWaiveReason')}
                  value={waiveReason}
                  onChange={(val) => setWaiveReason(val)}
                />
              )}
            </>
          )}
        </div>
      </AppDialog>
    )}

    {isRegisterOpen && occurrence?.seriesId && (
      <EduRegisterDialog
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        workspaceId={workspaceId}
        seriesId={occurrence.seriesId}
        date={occurrence.date}
        sessionLabel={
          [
            subjectElement?.title || classElement?.title || '',
            occurrence.startTime,
          ]
            .filter(Boolean)
            .join(' — ')
        }
        isToday={isSessionToday}
        isBookable={Boolean(series?.bookable)}
      />
    )}
  </>
  )
}
